import { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Typography from '@material-ui/core/Typography';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { ReactComponent as DropdownIcon } from '../../../assets/icons/arrow-down-dark.svg';
import { isArrayEmpty } from '../../../utility/helpers';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  statusWrapper: {
    width: 'fit-content',
    height: 'fit-content',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: 0,
    borderRadius: 4,
    flexShrink: 0,
  },
  root: {
    boxSizing: 'border-box',
    borderRadius: spacing(1),
    display: 'flex',
    alignItems: 'center',
    padding: spacing(1, 2),
    width: 'auto',
  },
  rootSmall: {
    padding: spacing(0.5, 1),
  },
  doubleApproveRoot: {
    borderRadius: spacing(0, 1, 1, 0),
  },
  status: {
    color: 'inherit',
    display: 'flex',
    alignItems: 'center',
    fontSize: 10,
    lineHeight: '12px',
    textTransform: 'uppercase',
    userSelect: 'none',
  },
  statusSmall: {
    fontSize: 8,
  },
  clickable: {
    cursor: 'pointer',
  },
  dropdownIcon: {
    flexShrink: 0,
    marginLeft: spacing(1.5),
    cursor: 'pointer',
    '& path': {
      fill: ({ status }) => {
        if (status.key === 'toDo') {
          return primary.blue2;
        }

        if (status === 'inProgress') {
          return primary.orange1;
        }

        return primary.green15;
      },
    },
  },
  toDo: {
    background: primary.blue11,
    color: primary.blue2,
  },
  done: {
    background: primary.green16,
    color: primary.green15,
  },
  completed: {
    background: primary.green16,
    color: primary.green15,
  },
  inProgress: {
    background: primary.beige1,
    color: primary.orange1,
  },
  pending: {
    background: primary.blue11,
    color: primary.blue2,
  },
  draft: {
    background: primary.blue11,
    color: primary.bluish1,
  },
  scheduled: {
    background: primary.blue11,
    color: primary.blue2,
  },
  requested: {
    backgroundColor: primary.orange,
    color: primary.white,
  },
  requestedAbsence: {
    backgroundColor: primary.red8,
    color: primary.bluish3,
  },
  halfApproved: {
    backgroundColor: primary.red8,
    color: primary.bluish3,
  },
  approved: {
    backgroundColor: primary.green1,
    color: primary.white,
  },
  approvedAbsence: {
    backgroundColor: primary.green17,
    color: primary.bluish3,
  },
  activeWithoutAccess: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.green1}`,
    color: primary.green1,
  },
  activeInvited: {
    backgroundColor: primary.white,
    border: `1px solid ${primary.green1}`,
    color: primary.green1,
  },
  activeWithAccess: {
    backgroundColor: primary.green1,
    border: `1px solid ${primary.green1}`,
    color: primary.white,
  },
  deactivated: {
    backgroundColor: primary.gray3,
    color: primary.bluish1,
  },
  menuItem: {
    fontFamily: 'ProximaNova-Bold',
  },
  rejected: {
    backgroundColor: primary.gray3,
    border: `1px solid ${primary.red1}`,
    color: primary.red1,
  },
  total: {
    backgroundColor: primary.blue12,
    color: primary.bluish3,
  },
  day: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minWidth: 20,
    minHeight: 20,
    borderRadius: spacing(1, 0, 0, 1),
    overflow: 'auto',
  },
  pendingAbsenceDoubleApprove: {
    background: `linear-gradient(45deg,
        ${primary.red8} 0 48%,
        ${primary.white} 48% 52%,
        ${primary.red8} 52% 100%)`,
  },
  halfApprovedAbsence: {
    background: `linear-gradient(45deg,
        ${primary.green17} 0 48%,
        ${primary.white} 48% 52%,
        ${primary.red8} 52% 100%)`,
  },
  approvedAbsenceDoubleApprove: {
    background: `linear-gradient(45deg,
        ${primary.green17} 0 48%,
        ${primary.white} 48% 52%,
        ${primary.green17} 52% 100%)`,
  },
}));

const REQUESTED_DEFAULT_KEY = 'requestedAbsence';
const HALF_APPROVED_DEFAULT_KEY = 'halfApproved';
const APPROVED_DEFAULT_KEY = 'approvedAbsence';
const REJECTED_DEFAULT_KEY = 'rejected';

const StatusIndicator = ({
  className = null,
  status = null,
  statuses = [],
  isSmall = false,
  isSelectable = false,
  isDoubleApprove,
  onSelect = () => {},
}) => {
  const classes = useStyles({ status });

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = e => {
    if (!isSelectable) return;

    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = e => {
    e.stopPropagation();
    setAnchorEl(null);
  };

  const handleSelect = e => {
    const { value } = e.target;

    if (value !== status.value) {
      onSelect(value);
    }

    handleClose(e);
  };

  return (
    <div className={classNames(classes.statusWrapper, className)}>
      {isDoubleApprove && (
        <div
          className={classNames({
            [classes.day]: REJECTED_DEFAULT_KEY !== status?.key,
            [classes.pendingAbsenceDoubleApprove]:
              REQUESTED_DEFAULT_KEY === status?.key,
            [classes.halfApprovedAbsence]:
              HALF_APPROVED_DEFAULT_KEY === status?.key,
            [classes.approvedAbsenceDoubleApprove]:
              APPROVED_DEFAULT_KEY === status?.key,
          })}
        />
      )}
      <div
        className={classNames(classes.root, {
          [classes.rootSmall]: isSmall,
          [classes[status?.key]]: status,
          [classes.doubleApproveRoot]:
            isDoubleApprove &&
            [
              REQUESTED_DEFAULT_KEY,
              HALF_APPROVED_DEFAULT_KEY,
              APPROVED_DEFAULT_KEY,
            ].includes(status?.key),
        })}
        onClick={handleOpen}
      >
        <Typography
          variant="overline"
          className={classNames(classes.status, {
            [classes.statusSmall]: isSmall,
            [classes.clickable]: isSelectable,
          })}
        >
          {status.name}
        </Typography>
        {isSelectable && <DropdownIcon className={classes.dropdownIcon} />}
        {isSelectable && !isArrayEmpty(statuses) && (
          <Menu
            anchorEl={anchorEl}
            open={!!anchorEl}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            onClose={handleClose}
          >
            {statuses.map(s => (
              <MenuItem
                key={s.key}
                classes={{ root: classes.menuItem }}
                value={s.value}
                selected={s.value === status.value}
                onClick={handleSelect}
              >
                {s.name}
              </MenuItem>
            ))}
          </Menu>
        )}
      </div>
    </div>
  );
};

StatusIndicator.propTypes = {
  className: PropTypes.string,
  status: PropTypes.shape({}),
  statuses: PropTypes.arrayOf(PropTypes.shape({})),
  isSelectable: PropTypes.bool,
  isSmall: PropTypes.bool,
  onSelect: PropTypes.func,
};

export default StatusIndicator;
